/**
 * LP Gauge
 * Drives the looping 270° output gauge on the lp-gauge section: segment
 * colour/glow (sampled from the merchant's arc gradient), needle rotation,
 * centre number and caption all update from a single progress value each
 * frame, so they stay perfectly in sync. Always renders a gradient-accurate
 * static frame (even under prefers-reduced-motion, which just skips
 * starting the animation loop) and only animates while on screen.
 */
(function () {
  'use strict';

  var EASINGS = {
    linear: function (t) { return t; },
    'ease-in': cubicBezier(0.4, 0, 1, 1),
    'ease-out': cubicBezier(0, 0, 0.2, 1),
    'ease-in-out': cubicBezier(0.65, 0, 0.35, 1),
    theme: cubicBezier(0.25, 0.46, 0.45, 0.94)
  };

  function cubicBezier(x1, y1, x2, y2) {
    // Approximates the y-value of a CSS cubic-bezier(x1,y1,x2,y2) timing
    // function at a given input time t, via binary search on x(t) = t.
    function xForT(t) {
      var mt = 1 - t;
      return 3 * mt * mt * t * x1 + 3 * mt * t * t * x2 + t * t * t;
    }
    function yForT(t) {
      var mt = 1 - t;
      return 3 * mt * mt * t * y1 + 3 * mt * t * t * y2 + t * t * t;
    }
    return function (time) {
      var lo = 0, hi = 1, t = time;
      for (var i = 0; i < 12; i++) {
        t = (lo + hi) / 2;
        if (xForT(t) < time) { lo = t; } else { hi = t; }
      }
      return yForT(t);
    };
  }

  // ── Gauge geometry ──────────────────────────────────────────────────
  // Matches the 270° arc drawn in lp-gauge.liquid (centre 140,130, radius
  // 90, running from 225° through the top to -45°). Liquid has no trig
  // functions, so the light-up block paths are computed here instead.
  var GAUGE_CX = 140, GAUGE_CY = 130, GAUGE_R = 90;
  var START_ANGLE_DEG = 225, SWEEP_ANGLE_DEG = 270;
  // Flat (butt) caps on each segment mean the gap is exactly this many
  // degrees of empty arc — no cap-overlap fudge factor needed.
  var SEGMENT_GAP_DEG = 6;
  var SVG_NS = 'http://www.w3.org/2000/svg';

  function polarPoint(thetaDeg) {
    var rad = (thetaDeg * Math.PI) / 180;
    return [GAUGE_CX + GAUGE_R * Math.cos(rad), GAUGE_CY - GAUGE_R * Math.sin(rad)];
  }

  function buildSegments(group, count) {
    var blockDeg = (SWEEP_ANGLE_DEG - SEGMENT_GAP_DEG * (count - 1)) / count;
    var largeArcFlag = blockDeg > 180 ? 1 : 0;
    var segments = [];
    for (var i = 0; i < count; i++) {
      var thetaStart = START_ANGLE_DEG - i * (blockDeg + SEGMENT_GAP_DEG);
      var thetaEnd = thetaStart - blockDeg;
      var p1 = polarPoint(thetaStart);
      var p2 = polarPoint(thetaEnd);
      var path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('class', 'lp-gauge__segment');
      path.setAttribute('d', 'M' + p1[0] + ' ' + p1[1] + ' A' + GAUGE_R + ' ' + GAUGE_R + ' 0 ' + largeArcFlag + ' 1 ' + p2[0] + ' ' + p2[1]);
      group.appendChild(path);
      segments.push(path);
    }
    return segments;
  }

  // ── Gradient parsing ────────────────────────────────────────────────
  // Shopify's color_background setting returns either a solid colour or a
  // CSS gradient string (e.g. "linear-gradient(90deg, rgba(r,g,b,a) 0%,
  // rgba(r,g,b,a) 100%)"). Only the stop colours + positions matter here —
  // the gradient's own angle/shape is ignored, since colour is applied to
  // SVG shapes (arc segments, needle) via their own geometry, not a CSS
  // background.
  function parseChannels(str) {
    var parts = str.split(',').map(function (n) { return parseFloat(n); });
    return [parts[0] || 0, parts[1] || 0, parts[2] || 0, parts.length > 3 ? parts[3] : 1];
  }

  function parseAnyColor(str) {
    var rgbaMatch = /rgba?\(([^)]+)\)/.exec(str);
    if (rgbaMatch) return parseChannels(rgbaMatch[1]);
    var hexMatch = /#([0-9a-fA-F]{3,8})/.exec(str);
    if (hexMatch) {
      var hex = hexMatch[1];
      if (hex.length === 3) hex = hex.split('').map(function (c) { return c + c; }).join('');
      var num = parseInt(hex.slice(0, 6), 16) || 0;
      return [(num >> 16) & 255, (num >> 8) & 255, num & 255, 1];
    }
    return null;
  }

  function parseGradientStops(str) {
    str = str || '';
    var stops = [];
    var re = /rgba?\(([^)]+)\)\s*(-?[\d.]+)%/g;
    var m;
    while ((m = re.exec(str))) {
      stops.push({ pos: parseFloat(m[2]) / 100, rgba: parseChannels(m[1]) });
    }
    if (!stops.length) {
      var solid = parseAnyColor(str) || [255, 161, 10, 1];
      stops.push({ pos: 0, rgba: solid }, { pos: 1, rgba: solid });
    }
    stops.sort(function (a, b) { return a.pos - b.pos; });
    return stops;
  }

  function sampleGradient(stops, fraction) {
    fraction = Math.max(0, Math.min(1, fraction));
    if (fraction <= stops[0].pos) return stops[0].rgba;
    var last = stops[stops.length - 1];
    if (fraction >= last.pos) return last.rgba;
    for (var i = 0; i < stops.length - 1; i++) {
      var a = stops[i], b = stops[i + 1];
      if (fraction >= a.pos && fraction <= b.pos) {
        var t = b.pos === a.pos ? 0 : (fraction - a.pos) / (b.pos - a.pos);
        return [
          a.rgba[0] + (b.rgba[0] - a.rgba[0]) * t,
          a.rgba[1] + (b.rgba[1] - a.rgba[1]) * t,
          a.rgba[2] + (b.rgba[2] - a.rgba[2]) * t,
          a.rgba[3] + (b.rgba[3] - a.rgba[3]) * t
        ];
      }
    }
    return last.rgba;
  }

  function rgbaString(rgba) {
    return 'rgba(' + Math.round(rgba[0]) + ',' + Math.round(rgba[1]) + ',' + Math.round(rgba[2]) + ',' + rgba[3] + ')';
  }

  var arcGradCounter = 0;

  // A single interpolated colour re-sampled every frame only ever shows the
  // narrow slice of the gradient between the low value and 100% (value
  // never goes lower), which barely looks different. A real multi-stop SVG
  // gradient spanning the full 0–100% arc, built once from the same stops,
  // shows the whole gradient sweep instead.
  function buildArcGradient(defs, stops) {
    var p1 = polarPoint(START_ANGLE_DEG);
    var p2 = polarPoint(START_ANGLE_DEG - SWEEP_ANGLE_DEG);
    var grad = document.createElementNS(SVG_NS, 'linearGradient');
    var id = 'lp-gauge-arc-grad-' + (arcGradCounter++);
    grad.setAttribute('id', id);
    grad.setAttribute('gradientUnits', 'userSpaceOnUse');
    grad.setAttribute('x1', p1[0]); grad.setAttribute('y1', p1[1]);
    grad.setAttribute('x2', p2[0]); grad.setAttribute('y2', p2[1]);
    stops.forEach(function (stop) {
      var stopEl = document.createElementNS(SVG_NS, 'stop');
      stopEl.setAttribute('offset', (stop.pos * 100) + '%');
      stopEl.setAttribute('stop-color', rgbaString(stop.rgba));
      grad.appendChild(stopEl);
    });
    defs.appendChild(grad);
    return id;
  }

  function initGauge(widget, reduceMotion) {
    var fillPath = widget.querySelector('.lp-gauge__fill');
    var trackPath = widget.querySelector('.lp-gauge__track');
    var segmentsGroup = widget.querySelector('.lp-gauge__segments');
    var glowPath = widget.querySelector('.lp-gauge__glow');
    var needle = widget.querySelector('.lp-gauge__needle');
    var needleHub = widget.querySelector('.lp-gauge__needle-hub');
    var needleGradient = widget.querySelector('.lp-gauge__svg linearGradient');
    var numEl = widget.querySelector('.lp-gauge__num');
    var captionEl = widget.querySelector('.lp-gauge__caption');
    // The Before/After swatches live in .lp-gauge__content, a sibling of
    // .lp-gauge__visual (which contains this widget) — not a descendant of
    // the widget itself. Querying from the widget silently finds nothing,
    // so scope the search to the whole section instead.
    var sectionRoot = widget.closest('.lp-gauge') || document;
    var beforeSwatch = sectionRoot.querySelector('[data-swatch="before"]');
    var afterSwatch = sectionRoot.querySelector('[data-swatch="after"]');
    if (!fillPath || !segmentsGroup || !needle || !numEl || !captionEl) return;

    var low = parseFloat(widget.dataset.low) || 70;
    var holdFull = parseFloat(widget.dataset.holdFull) || 1400;
    var sweepDown = parseFloat(widget.dataset.sweepDown) || 1500;
    var holdLow = parseFloat(widget.dataset.holdLow) || 1500;
    var sweepUp = parseFloat(widget.dataset.sweepUp) || 1600;
    var easeDown = EASINGS[widget.dataset.easeDown] || EASINGS.theme;
    var easeUp = EASINGS[widget.dataset.easeUp] || EASINGS.theme;
    var captionFull = widget.dataset.captionFull || '';
    var captionLoss = widget.dataset.captionLoss || '';
    var segmentCount = parseInt(widget.dataset.segments, 10) || 18;
    var glowMaxOpacity = (parseFloat(widget.dataset.glowOpacity) || 0) / 100;

    var arcStops = parseGradientStops(widget.dataset.arcGradient);
    var needleStops = parseGradientStops(widget.dataset.needleGradient);

    // Read the arc length off the continuous fallback path (still the exact
    // same "d") before swapping over to the segmented blocks below.
    var len = fillPath.getTotalLength();
    if (glowPath) glowPath.style.strokeDasharray = len;

    fillPath.style.display = 'none';
    if (trackPath) trackPath.style.display = 'none';

    var segments = buildSegments(segmentsGroup, segmentCount);
    // Each segment's lit colour is fixed to its own position along the arc
    // gradient (not the live animated value) — only whether it's lit or not
    // changes over time, so a "wave" of fixed colours lights up in sequence.
    var segmentColors = segments.map(function (_, i) {
      return rgbaString(sampleGradient(arcStops, (i + 0.5) / segments.length));
    });

    if (needleGradient) {
      while (needleGradient.firstChild) needleGradient.removeChild(needleGradient.firstChild);
      needleStops.forEach(function (stop) {
        var stopEl = document.createElementNS(SVG_NS, 'stop');
        stopEl.setAttribute('offset', (stop.pos * 100) + '%');
        stopEl.setAttribute('stop-color', rgbaString(stop.rgba));
        needleGradient.appendChild(stopEl);
      });
    }

    // The hub is a plain filled circle, which can't reference the pointer's
    // fade gradient the way the tip's fill does — sampling that gradient
    // outside its own coordinate range would just clamp to its nearest
    // stop, which for the default fade-from-transparent gradient could
    // make the hub invisible. Use the gradient's fully-opaque end colour
    // as a solid fill instead, so hub and pointer read as one needle.
    var needleHubStop = needleStops[needleStops.length - 1];
    if (needleHub && needleHubStop) needleHub.style.fill = rgbaString(needleHubStop.rgba);

    // "Before Clean" shows the unlit colour (most of the ring is unlit at
    // the dirty/low value) and "After Clean" shows the pure 100% end of
    // the arc gradient — not an in-between sample, since the key is meant
    // to read as the two end-states, not whatever segment happens to sit
    // at the low value's boundary.
    var unlitColor = parseAnyColor(widget.dataset.unlitColor) || [107, 66, 38, 1];
    if (beforeSwatch) beforeSwatch.style.background = rgbaString(unlitColor);
    if (afterSwatch) afterSwatch.style.background = rgbaString(sampleGradient(arcStops, 1));

    if (glowPath) {
      var defs = widget.querySelector('.lp-gauge__svg defs');
      if (defs) glowPath.style.stroke = 'url(#' + buildArcGradient(defs, arcStops) + ')';
    }

    function render(value) {
      var t = Math.max(0, Math.min(1, (value - low) / (100 - low)));

      if (glowPath) {
        glowPath.style.strokeDashoffset = len * (1 - value / 100);
        glowPath.style.opacity = glowMaxOpacity * t;
      }

      var litCount = Math.round((value / 100) * segments.length);
      for (var i = 0; i < segments.length; i++) {
        segments[i].style.stroke = i < litCount ? segmentColors[i] : '';
      }

      // The needle is drawn pointing straight up (CSS rotate(0) = angle 90°
      // in polarPoint terms), so its CSS rotation is 90° minus the target
      // angle: rotate = 90 - (START_ANGLE - SWEEP_ANGLE * value/100).
      var needleRotate = 90 - START_ANGLE_DEG + SWEEP_ANGLE_DEG * (value / 100);
      needle.style.transform = 'rotate(' + needleRotate + 'deg)';
      numEl.textContent = Math.round(value);
    }

    // Reduced-motion users never animate, so their permanent frame should be
    // the full-output end state. Everyone else starts on the low/dirty
    // value and waits until the widget scrolls into view before animating.
    if (reduceMotion) {
      render(100);
      captionEl.textContent = captionFull;
      return;
    }
    render(low);
    captionEl.textContent = captionLoss;

    var rafId = null;
    var phaseStart = null;
    // Starts on holdLow (not sweepUp) so entering the viewport holds at the
    // low value long enough to actually register before climbing — jumping
    // straight into the sweep left only a single imperceptible frame at the
    // low value.
    var phase = 'holdLow';

    var phases = {
      holdFull: { duration: holdFull, onStart: function () { render(100); captionEl.textContent = captionFull; }, next: 'sweepDown' },
      sweepDown: { duration: sweepDown, onStart: function () { captionEl.textContent = captionLoss; }, onTick: function (p) { render(100 - (100 - low) * easeDown(p)); }, next: 'holdLow' },
      holdLow: { duration: holdLow, onStart: function () { render(low); }, next: 'sweepUp' },
      sweepUp: { duration: sweepUp, onStart: function () { captionEl.textContent = captionFull; }, onTick: function (p) { render(low + (100 - low) * easeUp(p)); }, next: 'holdFull' }
    };

    function tick(now) {
      if (phaseStart === null) {
        phaseStart = now;
        phases[phase].onStart();
      }
      var elapsed = now - phaseStart;
      var def = phases[phase];
      var p = Math.min(elapsed / def.duration, 1);
      if (def.onTick) def.onTick(p);

      if (elapsed >= def.duration) {
        phase = def.next;
        phaseStart = null;
      }
      rafId = requestAnimationFrame(tick);
    }

    function start() {
      if (rafId !== null) return;
      // phase starts as 'holdLow' so the first entry into the viewport
      // holds at the low value before sweeping up; stop() below leaves
      // phase wherever the loop was paused, so re-entering later just
      // resumes it.
      rafId = requestAnimationFrame(tick);
    }

    function stop() {
      if (rafId === null) return;
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { start(); } else { stop(); }
        });
      }, { threshold: 0.2 });
      observer.observe(widget);
    } else {
      start();
    }
  }

  function init() {
    var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    document.querySelectorAll('.lp-gauge__widget[data-lp-gauge]:not([data-lp-gauge-init])').forEach(function (widget) {
      widget.setAttribute('data-lp-gauge-init', '');
      initGauge(widget, reduceMotion);
    });
  }

  init();

  // Re-init after Shopify theme editor reloads the section
  document.addEventListener('shopify:section:load', init);
})();
